import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from '../../config/auth.config';
import { ActivatedRoute, Router } from '@angular/router';
import { authConfig } from '../../config/constants';
import { catchError, switchMap } from 'rxjs/operators';
import { throwError, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StableLinkIntegrationService {
  constructor(private http: HttpClient, private oauthService: OAuthService) {}

  public get(url: string, options?: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': '*/*',
      }),
    };
    console.log(httpOptions);
    return this.http.get(url, httpOptions);
  }

    public post(url: string, data: any, options?: any): Observable<any> {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Accept': '*/*',
        }),
      };
      return this.http.post(url, data, httpOptions).pipe(
        catchError((error) => {
          console.error(error);
          console.log("Error in post request, maybe token has expired... retrying...");
          // Don't clear sessionStorage, let OAuth service handle token refresh
          return this.oauthService.refreshToken().then(() => {
            console.log("Token refreshed successfully, retrying request");
            return this.http.post(url, data, httpOptions);
          }).catch(() => {
            console.log("Token refresh failed, redirecting to login");
            this.oauthService.initLoginFlow();
            return throwError(error);
          });
        })
      );
    }


  public put(url: string, data: any, options?: any) {
    return this.http.put(url, data, options);
  }
  
  public delete(url: string, options?: any) {
    return this.http.delete(url, options);
  }

  public login(): Observable<any> {
    if (authConfig.IS_SSO_Integrated) {
      return new Observable(observer => {
        this.oauthService.configure(authCodeFlowConfig);
        
        // Add event listeners for better error handling
        this.oauthService.events.subscribe(event => {
          if (event.type === 'silent_refresh_error') {
            console.error('Silent refresh failed:', event);
            // Try to refresh token manually
            this.oauthService.refreshToken().catch(() => {
              console.log('Manual token refresh failed, redirecting to login');
              this.oauthService.initLoginFlow();
            });
          }
        });
        
        // Check if this is a silent refresh callback by looking at URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const urlHash = window.location.hash;
        
        // If we have OAuth parameters (code, state, etc.) and we're in an iframe, this is likely a silent refresh
        const isInIframe = window.self !== window.top;
        const hasOAuthParams = urlParams.has('code') || urlParams.has('error') || urlHash.includes('access_token') || urlHash.includes('code');
        
        if (isInIframe && hasOAuthParams) {
          console.log('Detected silent refresh callback, processing...');
          // This is a silent refresh callback, just try to process the token
          this.oauthService.tryLogin({
            onTokenReceived: function (context) {
              console.log('Silent refresh token received successfully');
              window.location.hash = ''; // Clean up URL
              observer.next(null);
              observer.complete();
            },
          }).then(() => {
            // If tryLogin doesn't call onTokenReceived, still complete
            observer.next(null);
            observer.complete();
          }).catch(error => {
            console.error('Error processing silent refresh:', error);
            observer.error(error);
          });
        } else {
          // Normal login flow
          this.oauthService.loadDiscoveryDocumentAndLogin()
            .then(() => {
              this.oauthService.tryLogin({
                onTokenReceived: function (context) {
                  window.location.hash = ''; // Remove access token from URL
                  observer.next(null); // Emit value to signal successful login
                  observer.complete(); // Complete the observable
                },
              });
            })
            .catch(error => {
              console.error('Error during login:', error);
              observer.error(error); // Emit error if login fails
            });
        }
      }).pipe(
        catchError(error => {
          console.error('Error during login:', error);
          sessionStorage.clear();
          return throwError(error); // Re-throw the error
        })
      );
    } else {
      // Return an observable that immediately throws an error if SSO is not integrated
      return throwError('SSO not integrated');
    }
  }

}
